import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAdminUserDetail, useAdminUserUpdate, useResetUserMfa } from "@/hooks/use-admin-users";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { TypeConfirmDialog } from "@/components/type-confirm-dialog";
import { UserProfileCard } from "@/components/user-detail/user-profile-card";
import { UserOrgsTable } from "@/components/user-detail/user-orgs-table";
import { UserMfaSection } from "@/components/user-detail/user-mfa-section";
import { UserActionsSection, type SystemRole } from "@/components/user-detail/user-actions-section";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/_admin/users/$userId")({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { t } = useTranslation("admin");
  const { userId } = Route.useParams();
  const { user: currentUser } = useAuth();
  const { data: user, isLoading, error } = useAdminUserDetail(userId);
  const updateMutation = useAdminUserUpdate();
  const resetMfaMutation = useResetUserMfa();

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState<SystemRole>("tester");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [mfaResetDialogOpen, setMfaResetDialogOpen] = useState(false);

  const isOwnAccount = currentUser?.id === userId;
  const isActive = !user?.deleted_at;

  function handleRoleSelect(role: SystemRole) {
    setPendingRole(role);
    setRoleDialogOpen(true);
  }

  function handleRoleConfirm() {
    updateMutation.mutate(
      { userId, body: { role: pendingRole } },
      {
        onSuccess: () => {
          toast.success(t("toasts.userRoleUpdated"));
          setRoleDialogOpen(false);
        },
        onError: (err) => {
          toast.error(err.message);
        },
      },
    );
  }

  function handleStatusToggle() {
    setStatusDialogOpen(true);
  }

  function handleStatusConfirm() {
    updateMutation.mutate(
      { userId, body: { active: !isActive } },
      {
        onSuccess: () => {
          toast.success(isActive ? t("toasts.userDeactivated") : t("toasts.userReactivated"));
          setStatusDialogOpen(false);
        },
        onError: (err) => {
          toast.error(err.message);
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-24 animate-pulse rounded bg-slate-200" />
        <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        Failed to load user: {error.message}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-slate-600">
        User not found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Link
        to="/users"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
          />
        </svg>
        Back to Users
      </Link>

      <UserProfileCard user={user} />
      <UserOrgsTable orgs={user.orgs} />
      <UserMfaSection
        mfaEnabled={user.mfa_enabled ?? false}
        onResetMfa={() => setMfaResetDialogOpen(true)}
      />
      <UserActionsSection
        currentRole={user.role}
        isOwnAccount={isOwnAccount}
        isActive={isActive}
        onRoleSelect={handleRoleSelect}
        onStatusToggle={handleStatusToggle}
      />

      {/* Confirm Role Dialog */}
      <ConfirmDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        onConfirm={handleRoleConfirm}
        title={t("userDetail.roleDialog.title")}
        message={t("userDetail.roleDialog.message", {
          name: user.full_name,
          role: t(`systemRole.${pendingRole}`),
        })}
        confirmText={t("userDetail.roleDialog.confirm")}
        variant="primary"
        isLoading={updateMutation.isPending}
      />

      {/* Confirm Status Dialog */}
      <ConfirmDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        onConfirm={handleStatusConfirm}
        title={
          isActive ? t("userDetail.deactivateDialog.title") : t("userDetail.reactivateDialog.title")
        }
        message={
          isActive
            ? t("userDetail.deactivateDialog.message", { name: user.full_name })
            : t("userDetail.reactivateDialog.message", { name: user.full_name })
        }
        confirmText={
          isActive
            ? t("userDetail.deactivateDialog.confirm")
            : t("userDetail.reactivateDialog.confirm")
        }
        variant={isActive ? "danger" : "primary"}
        isLoading={updateMutation.isPending}
      />

      {/* Type-to-confirm MFA reset */}
      <TypeConfirmDialog
        open={mfaResetDialogOpen}
        onClose={() => setMfaResetDialogOpen(false)}
        onConfirm={() => {
          resetMfaMutation.mutate(userId, {
            onSuccess: () => {
              toast.success(t("toasts.mfaReset"));
              setMfaResetDialogOpen(false);
            },
            onError: (err) => {
              toast.error(err.message);
            },
          });
        }}
        title={t("userDetail.mfaResetDialog.title")}
        message={t("userDetail.mfaResetDialog.message", { name: user.full_name })}
        confirmValue={user.email}
        confirmLabel={t("dialog.typeValueToConfirm", { value: user.email })}
        confirmText={t("userDetail.mfaResetDialog.confirm")}
        variant="danger"
        isLoading={resetMfaMutation.isPending}
      />
    </div>
  );
}
